import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { 
  FaSearch, 
  FaFilter, 
  FaDownload, 
  FaTrashAlt, 
  FaChevronLeft, 
  FaChevronRight, 
  FaSync, 
  FaUserGraduate, 
  FaExclamationTriangle,
  FaRegCalendarAlt,
  FaClock
} from "react-icons/fa";
import { toast, Toaster } from "react-hot-toast";

const ExamList = () => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtre durumları
  const [searchName, setSearchName] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSession, setSelectedSession] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  
  // Sayfalama
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // İstatistikler
  const [stats, setStats] = useState({
    total: 0,
    byClass: {},
    byDate: {},
    bySession: {}
  });

  const calculateStats = (data) => {
    const byClass = {};
    const byDate = {};
    const bySession = {};
    
    data.forEach(student => {
      // Sınıf istatistikleri
      byClass[student.class_level] = (byClass[student.class_level] || 0) + 1;
      
      // Tarih istatistikleri
      byDate[student.exam_date] = (byDate[student.exam_date] || 0) + 1;
      
      // Oturum istatistikleri
      const sessionKey = `${student.exam_date} ${student.exam_session}`;
      bySession[sessionKey] = (bySession[sessionKey] || 0) + 1;
    });
    
    setStats({
      total: data.length,
      byClass,
      byDate,
      bySession
    });
  };

  const fetchStudents = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem("authToken");
      
      if (!token) {
        setError("Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.");
        setLoading(false);
        return;
      }
      
      const timestamp = new Date().getTime();
      const response = await axios.get(`http://esnakademi.com/api/exam_list?_t=${timestamp}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (Array.isArray(response.data)) {
        setStudents(response.data);
        setFilteredStudents(response.data);
        calculateStats(response.data);
        toast.success("Öğrenci listesi başarıyla yüklendi");
      } else {
        console.error("Beklenen veri formatı alınamadı:", response.data);
        setError("Veri formatı beklenmedik şekilde. Lütfen daha sonra tekrar deneyin.");
      }
    } catch (error) {
      console.error("Veri çekilirken hata oluştu:", error);
      setError(`Veri yüklenirken bir hata oluştu: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteStudent = async (id, name) => {
    if (!window.confirm(`"${name}" öğrencisini silmek istediğinize emin misiniz?`)) {
      return;
    }
    
    const token = localStorage.getItem("authToken");
    
    try {
      const loadingToast = toast.loading(`"${name}" kaydı siliniyor...`);
      
      await axios.get(`http://esnakademi.com/api/exam_list/delete?id=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.dismiss(loadingToast);
      toast.success(`"${name}" kaydı başarıyla silindi`);
      fetchStudents();
    } catch (error) {
      console.error("Silme işlemi sırasında hata oluştu:", error);
      toast.error(`Silme işlemi başarısız: ${error.response?.data?.message || error.message}`);
    }
  };

  const filterStudents = () => {
    let filtered = students;

    if (searchName) {
      filtered = filtered.filter((student) =>
        student.student_name.toLowerCase().includes(searchName.toLowerCase()) ||
        student.student_id.includes(searchName)
      );
    }

    if (selectedClass) {
      filtered = filtered.filter(
        (student) => student.class_level === selectedClass
      );
    }

    if (selectedSession) {
      filtered = filtered.filter(
        (student) => student.exam_session === selectedSession
      );
    }

    if (selectedDate) {
      filtered = filtered.filter(
        (student) => student.exam_date === selectedDate
      );
    }

    setFilteredStudents(filtered);
    setCurrentPage(1); // Filtreleme sonrası ilk sayfaya dön
  };
  
  const resetFilters = () => {
    setSearchName("");
    setSelectedClass("");
    setSelectedSession("");
    setSelectedDate("");
    setFilteredStudents(students);
  };

  // İlk yükleme
  useEffect(() => {
    fetchStudents();
  }, []);

  // Filtreleme
  useEffect(() => {
    filterStudents();
  }, [searchName, selectedClass, selectedSession, selectedDate]);

  // Excel'e aktarma
  const exportToExcel = () => {
    const toastId = toast.loading("Excel dosyası hazırlanıyor...");
    
    try {
      const headers = [
        "TC Kimlik",
        "Öğrenci Adı",
        "Sınıf",
        "Baba Adı",
        "Baba Telefon",
        "Anne Adı",
        "Anne Telefon",
        "Okul",
        "Sınav Tarihi",
        "Sınav Saati"
      ];

      const rows = filteredStudents.map((student) => [
        student.student_id,
        student.student_name,
        student.class_level,
        student.father_name,
        student.father_phone,
        student.mother_name,
        student.mother_phone,
        student.school,
        student.exam_date,
        student.exam_session
      ]);

      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sınav Listesi");

      // Sütun genişliklerini ayarla
      const colWidths = [
        { wch: 15 }, // TC
        { wch: 30 }, // Ad
        { wch: 10 }, // Sınıf
        { wch: 30 }, // Baba
        { wch: 15 }, // Baba Tel
        { wch: 30 }, // Anne
        { wch: 15 }, // Anne Tel
        { wch: 30 }, // Okul
        { wch: 15 }, // Tarih
        { wch: 10 }  // Saat
      ];
      worksheet['!cols'] = colWidths;

      const fileName = `sinav_listesi_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      toast.dismiss(toastId);
      toast.success("Excel dosyası başarıyla indirildi");
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("Excel dosyası oluşturulurken bir hata oluştu");
      console.error("Excel oluşturma hatası:", error);
    }
  };
  
  // Sayfalama için öğrencileri dilimle
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredStudents.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  
  // Sayfa değiştirme
  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Tarih ve oturum seçeneklerini oluştur
  const dateOptions = [...new Set(students.map(s => s.exam_date))].sort();
  const sessionOptions = [...new Set(students.map(s => s.exam_session))].sort();
  const classOptions = ["5.sınıf", "6.sınıf", "7.sınıf", "8.sınıf"];

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <Toaster position="top-right" />
      
      <div className="max-w-7xl mx-auto">
        {/* Başlık */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 pb-4 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center">
            <FaUserGraduate className="mr-3 text-indigo-600" />
            Sınav Kayıtlı Öğrenci Listesi
          </h1>
          <div className="flex items-center mt-4 md:mt-0 space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center px-4 py-2 rounded-lg border ${
                showFilters ? "bg-indigo-100 border-indigo-500 text-indigo-700" : "bg-white text-gray-700"
              }`}
            >
              <FaFilter className="mr-2" />
              Filtreler
              {(searchName || selectedClass || selectedSession || selectedDate) && (
                <span className="ml-2 bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                  {(searchName ? 1 : 0) + (selectedClass ? 1 : 0) + (selectedSession ? 1 : 0) + (selectedDate ? 1 : 0)}
                </span>
              )}
            </button>
            <button
              onClick={fetchStudents}
              className="flex items-center px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200"
              disabled={loading}
            >
              <FaSync className={`mr-2 ${loading ? "animate-spin" : ""}`} />
              Yenile
            </button>
            <button
              onClick={exportToExcel}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              disabled={filteredStudents.length === 0}
            >
              <FaDownload className="mr-2" />
              Excel
            </button>
          </div>
        </div>

        {/* İstatistikler */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-indigo-500">
            <div className="text-sm text-gray-500">Toplam Öğrenci</div>
            <div className="text-2xl font-bold text-gray-800">{stats.total}</div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-green-500">
            <div className="text-sm text-gray-500">En Çok Kayıtlı Sınıf</div>
            <div className="text-2xl font-bold text-gray-800">
              {Object.keys(stats.byClass).reduce((a, b) => stats.byClass[a] > stats.byClass[b] ? a : b, '') || 'N/A'}
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-yellow-500">
            <div className="text-sm text-gray-500">En Yoğun Gün</div>
            <div className="text-2xl font-bold text-gray-800">
              {Object.keys(stats.byDate).reduce((a, b) => stats.byDate[a] > stats.byDate[b] ? a : b, '') || 'N/A'}
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-red-500">
            <div className="text-sm text-gray-500">En Yoğun Oturum</div>
            <div className="text-2xl font-bold text-gray-800">
              {Object.keys(stats.bySession).reduce((a, b) => stats.bySession[a] > stats.bySession[b] ? a : b, '') || 'N/A'}
            </div>
          </div>
        </div>

        {/* Filtreler */}
        {showFilters && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-gray-200 animate-fadeIn">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Öğrenci Adı / TC Kimlik</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Ara..."
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <FaSearch className="absolute left-3 top-3 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sınıf Seviyesi</label>
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Tüm Sınıflar</option>
                  {classOptions.map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sınav Günü</label>
                <div className="relative">
                  <select
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Tüm Günler</option>
                    {dateOptions.map(date => (
                      <option key={date} value={date}>{date}</option>
                    ))}
                  </select>
                  <FaRegCalendarAlt className="absolute left-3 top-3 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Oturum Saati</label>
                <div className="relative">
                  <select
                    value={selectedSession}
                    onChange={(e) => setSelectedSession(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Tüm Saatler</option>
                    {sessionOptions.map(session => (
                      <option key={session} value={session}>{session}</option>
                    ))}
                  </select>
                  <FaClock className="absolute left-3 top-3 text-gray-400" />
                </div>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={resetFilters}
                className="py-2 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Filtreleri Temizle
              </button>
            </div>
          </div>
        )}

        {/* Hata Durumu */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg mb-6">
            <div className="flex">
              <FaExclamationTriangle className="text-red-500 mr-3 flex-shrink-0 mt-1" />
              <div>
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Öğrenci Sayısı */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <div className="flex justify-between items-center">
            <p className="text-gray-700">
              Listelenen öğrenci sayısı: <span className="font-semibold">{filteredStudents.length}</span>
              {filteredStudents.length !== students.length && (
                <span className="text-gray-500"> (Toplam: {students.length})</span>
              )}
            </p>
            
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Sayfa başına:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="border border-gray-300 rounded-md px-2 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>

        {/* Yükleniyor */}
        {loading ? (
          <div className="flex justify-center items-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : filteredStudents.length ? (
          <>
            {/* Öğrenci Tablosu */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-4">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">TC Kimlik</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Öğrenci Adı</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sınıf</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Baba Bilgileri</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Anne Bilgileri</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Okul</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sınav Oturumu</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentItems.map((student) => (
                      <tr key={student.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{student.student_id}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-medium text-gray-900">{student.student_name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            {student.class_level}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{student.father_name}</div>
                          <div className="text-sm text-gray-500">{student.father_phone}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{student.mother_name}</div>
                          <div className="text-sm text-gray-500">{student.mother_phone}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{student.school}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{student.exam_date}</div>
                          <div className="text-sm text-indigo-600">{student.exam_session}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => deleteStudent(student.id, student.student_name)}
                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100 transition-colors"
                          >
                            <FaTrashAlt />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Sayfalama */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center bg-white px-4 py-3 rounded-lg shadow-sm">
                <div className="text-sm text-gray-700">
                  <span>{indexOfFirstItem + 1}</span>
                  <span> - </span>
                  <span>{Math.min(indexOfLastItem, filteredStudents.length)}</span>
                  <span> / </span>
                  <span>{filteredStudents.length}</span>
                  <span> öğrenci gösteriliyor</span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded-md ${
                      currentPage === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                    }`}
                  >
                    <FaChevronLeft />
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    // İlk sayfa, son sayfa ve mevcut sayfa civarındaki sayfaları göster
                    let pageNum;
                    if (totalPages <= 5) {
                      // 5 veya daha az sayfa varsa hepsini göster
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      // Başlangıçtaysa ilk 5 sayfayı göster
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      // Sondaysa son 5 sayfayı göster
                      pageNum = totalPages - 4 + i;
                    } else {
                      // Ortadaysa mevcut sayfanın etrafındaki sayfaları göster
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => paginate(pageNum)}
                        className={`px-3 py-1 rounded-md ${
                          currentPage === pageNum
                            ? "bg-indigo-600 text-white"
                            : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded-md ${
                      currentPage === totalPages
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                    }`}
                  >
                    <FaChevronRight />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          // Sonuç bulunamadı
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <FaSearch className="text-gray-400 text-5xl mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-800 mb-2">Sonuç bulunamadı</h3>
            <p className="text-gray-500 mb-4">Arama kriterlerinize uygun öğrenci bulunamadı.</p>
            <button
              onClick={resetFilters}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Filtreleri Temizle
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExamList;